import React, {useMemo} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {MaterialCommunityIcons, MaterialIcons} from '@expo/vector-icons';
import {useTheme} from '../helpers/ThemeContext';
import {withAlpha} from '../helpers/managementTheme';
import {Sparkline} from './premium/PremiumCharts';
import {DeltaPill} from './premium/PremiumKit';

/**
 * @file PremiumStatCard — the workspace "Happy Solutions" premium KPI tile.
 *
 * A single reusable stat card: airy rounded surface, a soft tinted icon chip, a
 * bold value + caption, an optional trend pill and — only when the caller hands
 * in a REAL series — a smooth sparkline / capsule bars along the bottom edge.
 * Cards without data deliberately render no chart (no decorative fake shapes).
 * Fully light/dark aware via {@link useTheme}; pure `react-native-svg`, no
 * platform branches.
 */

/**
 * Tone → two-stop icon-chip gradient. Callers pass a `tone` string or an
 * explicit `gradient` array; the last stop doubles as the chart accent tint.
 * @type {Object<string, string[]>}
 */
const TONE_GRADIENTS = {
    primary: ['#FB923C', '#F97316'],
    orange: ['#FB923C', '#F97316'],
    amber: ['#FCD34D', '#F59E0B'],
    success: ['#34D399', '#10B981'],
    info: ['#60A5FA', '#3B82F6'],
    sky: ['#38BDF8', '#0EA5E9'],
    violet: ['#A78BFA', '#7C3AED'],
    danger: ['#F87171', '#EF4444'],
    warning: ['#FBBF24', '#F59E0B'],
    pink: ['#F472B6', '#EC4899'],
    teal: ['#2DD4BF', '#0D9488'],
    slate: ['#94A3B8', '#64748B'],
};

/**
 * Mix a hex color toward white to derive a lighter gradient stop.
 * @param {string} hex `#rgb`/`#rrggbb` color.
 * @param {number} amt 0..1 blend toward white.
 * @returns {string} `#rrggbb`.
 */
const lighten = (hex, amt = 0.22) => {
    if (typeof hex !== 'string') {
        return hex;
    }
    let h = hex.replace('#', '');
    if (h.length === 3) {
        h = h.split('').map((c) => c + c).join('');
    }
    if (h.length !== 6) {
        return hex;
    }
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const mix = (v) => Math.round(v + (255 - v) * amt).toString(16).padStart(2, '0');

    return `#${mix(r)}${mix(g)}${mix(b)}`;
};

/**
 * Mini capsule bars for a real series (last bar emphasised).
 * @param {{data: number[], accent: string, height?: number}} props
 * @returns {JSX.Element}
 */
const MiniBars = ({data, accent, height = 40}) => {
    const series = (data || []).slice(-16).map((v) => Number(v) || 0);
    const max = Math.max(...series, 1);

    return (
        <View style={[styles.miniBars, {height}]}>
            {series.map((v, i) => (
                <View
                    key={i}
                    style={[
                        styles.miniBar,
                        {
                            height: Math.max(4, (v / max) * height),
                            backgroundColor: i === series.length - 1 ? accent : withAlpha(accent, 0.28),
                        },
                    ]}
                />
            ))}
        </View>
    );
};

/**
 * Premium KPI stat card (brand blueprint).
 *
 * @param {object} props
 * @param {string} props.icon Icon glyph name.
 * @param {'material'|'community'} [props.iconSet='material'] Vector-icon family.
 * @param {string} [props.tone='primary'] Preset tone key (see {@link TONE_GRADIENTS}).
 * @param {string[]} [props.gradient] Explicit 2-stop gradient; its last stop is the chip tint.
 * @param {string} [props.accent] Explicit tint (defaults to the tone's strong stop).
 * @param {string} props.title Small caption next to the icon.
 * @param {string|number} props.value Bold primary value.
 * @param {string} [props.caption] Muted sub-caption under the value.
 * @param {number} [props.delta] Percentage change, rendered as a pill beside the value.
 * @param {number[]} [props.chartData] Real series to plot; no chart when omitted.
 * @param {'line'|'bar'} [props.chartKind='line'] Chart style for `chartData`.
 * @param {number} [props.seed] Unused (kept so existing call sites stay valid).
 * @param {string|number} [props.width='48%'] Card width.
 * @param {Function} [props.onPress] Optional tap handler.
 * @param {object} [props.style] Extra container style.
 * @returns {JSX.Element}
 */
const PremiumStatCard = ({
    icon,
    iconSet = 'material',
    tone = 'primary',
    gradient,
    accent,
    title,
    value,
    caption,
    delta,
    chartData,
    chartKind = 'line',
    width = '48%',
    onPress,
    style,
}) => {
    const {theme, isDark} = useTheme();
    const grad = Array.isArray(gradient) && gradient.length >= 2
        ? gradient
        : (accent ? [lighten(accent, 0.24), accent] : (TONE_GRADIENTS[tone] || TONE_GRADIENTS.primary));
    const tint = accent || grad[grad.length - 1];
    const IconCmp = iconSet === 'community' ? MaterialCommunityIcons : MaterialIcons;
    const hasSeries = Array.isArray(chartData) && chartData.length > 1;

    const surface = useMemo(() => ({
        backgroundColor: theme.colors.surface,
        borderColor: isDark ? 'rgba(148,163,184,0.14)' : '#ECEEF4',
    }), [theme, isDark]);

    const Container = onPress ? TouchableOpacity : View;

    return (
        <Container
            {...(onPress ? {activeOpacity: 0.88, onPress} : {})}
            style={[styles.card, surface, isDark ? styles.shadowDark : styles.shadowLight, {width}, style]}
        >
            <View style={styles.headerRow}>
                <View style={[styles.iconChip, {backgroundColor: withAlpha(tint, isDark ? 0.2 : 0.12)}]}>
                    <IconCmp name={icon} size={20} color={tint} />
                </View>
                {!!title && (
                    <Text style={[styles.title, {color: theme.colors.subText}]} numberOfLines={2}>
                        {title}
                    </Text>
                )}
            </View>

            <Text style={[styles.value, {color: theme.colors.text}]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>
                {value == null ? '' : String(value)}
            </Text>
            {(delta != null || !!caption) && (
                <View style={styles.captionRow}>
                    {delta != null && <DeltaPill delta={delta} />}
                    {!!caption && (
                        <Text style={[styles.caption, {color: theme.colors.subText}]} numberOfLines={1}>
                            {caption}
                        </Text>
                    )}
                </View>
            )}

            {hasSeries && (
                <View style={styles.chart}>
                    {chartKind === 'bar'
                        ? <MiniBars data={chartData} accent={tint} />
                        : <Sparkline data={chartData} color={tint} dotRing={theme.colors.surface} height={44} strokeWidth={2.2} />}
                </View>
            )}
        </Container>
    );
};

const styles = StyleSheet.create({
    card: {
        borderRadius: 24,
        padding: 16,
        borderWidth: 1,
        overflow: 'hidden',
        minHeight: 124,
    },
    shadowLight: {
        shadowColor: '#1E2A4A',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.07,
        shadowRadius: 16,
        elevation: 3,
    },
    shadowDark: {
        shadowColor: '#000000',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.3,
        shadowRadius: 16,
        elevation: 4,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 14,
    },
    iconChip: {
        width: 40,
        height: 40,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        flex: 1,
        fontSize: 13,
        fontWeight: '600',
    },
    value: {
        fontSize: 26,
        fontWeight: '800',
        letterSpacing: -0.6,
    },
    captionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 6,
    },
    caption: {
        fontSize: 12.5,
        fontWeight: '500',
        flexShrink: 1,
    },
    chart: {
        marginTop: 12,
    },
    miniBars: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 3,
    },
    miniBar: {
        flex: 1,
        borderRadius: 4,
    },
});

export {TONE_GRADIENTS};
export default PremiumStatCard;
